// @ts-nocheck
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  LANGUAGE_STORAGE_KEY,
  normalizeLanguage,
  translateUiText,
  UiLanguage,
} from './translations';

type I18nContextValue = {
  language: UiLanguage;
  preferredLanguage: UiLanguage;
  setLanguage: (language: UiLanguage, persist?: boolean) => void;
  resetToPreference: () => void;
  t: (text: string) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

const textState = new WeakMap<Text, { original: string; lastTranslated: string }>();
const attributeState = new WeakMap<Element, Map<string, { original: string; lastTranslated: string }>>();
const ATTRIBUTES = ['placeholder', 'title', 'aria-label'];

const shouldIgnoreElement = (element: Element | null) => {
  if (!element) return false;
  return Boolean(element.closest('[data-i18n-ignore="true"], script, style, code, pre'));
};

const translateTextNode = (node: Text, language: UiLanguage, internalWrite: { current: boolean }) => {
  if (shouldIgnoreElement(node.parentElement)) return;

  const current = node.nodeValue ?? '';
  let state = textState.get(node);
  if (!state) {
    state = { original: current, lastTranslated: current };
  } else if (!internalWrite.current && current !== state.lastTranslated && current !== state.original) {
    state.original = current;
  }

  const translated = translateUiText(state.original, language);
  state.lastTranslated = translated;
  textState.set(node, state);

  if (current !== translated) {
    internalWrite.current = true;
    node.nodeValue = translated;
    internalWrite.current = false;
  }
};

const translateAttributes = (element: Element, language: UiLanguage, internalWrite: { current: boolean }) => {
  if (shouldIgnoreElement(element)) return;
  let states = attributeState.get(element);
  if (!states) states = new Map();

  const attrs = [...ATTRIBUTES];
  if (element instanceof HTMLInputElement && ['button', 'submit', 'reset'].includes(element.type)) {
    attrs.push('value');
  }

  attrs.forEach((attribute) => {
    if (!element.hasAttribute(attribute)) return;
    const current = element.getAttribute(attribute) ?? '';
    let state = states.get(attribute);
    if (!state) {
      state = { original: current, lastTranslated: current };
    } else if (!internalWrite.current && current !== state.lastTranslated && current !== state.original) {
      state.original = current;
    }

    const translated = translateUiText(state.original, language);
    state.lastTranslated = translated;
    states.set(attribute, state);

    if (current !== translated) {
      internalWrite.current = true;
      element.setAttribute(attribute, translated);
      internalWrite.current = false;
    }
  });

  attributeState.set(element, states);
};

const translateTree = (root: Node, language: UiLanguage, internalWrite: { current: boolean }) => {
  if (root.nodeType === Node.TEXT_NODE) {
    translateTextNode(root as Text, language, internalWrite);
    return;
  }

  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return;

  const element = root.nodeType === Node.ELEMENT_NODE ? root as Element : null;
  if (element && shouldIgnoreElement(element)) return;
  if (element) translateAttributes(element, language, internalWrite);

  root.childNodes.forEach((child) => translateTree(child, language, internalWrite));
};

export const I18nProvider = ({ children }) => {
  const initial = normalizeLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY), 'PT');
  const [preferredLanguage, setPreferredLanguage] = useState<UiLanguage>(initial);
  const [language, setActiveLanguage] = useState<UiLanguage>(initial);
  const internalWrite = useRef(false);

  const setLanguage = useCallback((nextLanguage: UiLanguage, persist = true) => {
    const normalized = normalizeLanguage(nextLanguage, 'PT');
    setActiveLanguage(normalized);
    if (persist) {
      setPreferredLanguage(normalized);
      localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
    }
  }, []);

  const resetToPreference = useCallback(() => {
    const preferred = normalizeLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY), preferredLanguage);
    setPreferredLanguage(preferred);
    setActiveLanguage(preferred);
  }, [preferredLanguage]);

  const t = useCallback((text: string) => translateUiText(text, language), [language]);

  useLayoutEffect(() => {
    document.documentElement.lang = language === 'PT' ? 'pt-PT' : language === 'ES' ? 'es' : 'en';
    if (document.body) translateTree(document.body, language, internalWrite);
  }, [language]);

  useEffect(() => {
    if (!document.body) return undefined;

    const observer = new MutationObserver((mutations) => {
      if (internalWrite.current) return;
      for (const mutation of mutations) {
        if (mutation.type === 'characterData') {
          translateTree(mutation.target, language, internalWrite);
        }
        if (mutation.type === 'attributes') {
          translateTree(mutation.target, language, internalWrite);
        }
        mutation.addedNodes.forEach((node) => translateTree(node, language, internalWrite));
      }
    });

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: [...ATTRIBUTES, 'value'],
    });

    return () => observer.disconnect();
  }, [language]);

  const value = useMemo(() => ({
    language,
    preferredLanguage,
    setLanguage,
    resetToPreference,
    t,
  }), [language, preferredLanguage, resetToPreference, setLanguage, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside I18nProvider');
  return context;
};
